'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import { IconUpload, IconX } from '@tabler/icons-react';
import { Scissors, Video, Volume2, VolumeOff } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import type { DanceTemplate } from '@/config/dance-templates';
import { Button } from '@/shared/components/ui/button';
import { useAppContext } from '@/shared/contexts/app';
import { computeFileMD5 } from '@/shared/lib/client-hash';
import { cn } from '@/shared/lib/utils';
import { TemplatePickerDialog } from './template-picker-dialog';
import { TikTokLinkDialog, TikTokResolveResult } from './tiktok-link-dialog';
import { VideoClipEditorDialog } from './video-clip-editor-dialog';
import { VideoZoomButton } from './video-zoom-button';
export type VideoUploadStatus = 'idle' | 'uploading' | 'uploaded' | 'error';
export interface VideoUploaderValue {
  id: string;
  preview: string;
  url?: string;
  originalUrl?: string;
  originalDuration?: number;
  status: VideoUploadStatus;
  size?: number;
  duration?: number; // Video duration in seconds
  clipStart?: number;
  clipEnd?: number;
}
interface VideoUploaderProps {
  maxSizeMB?: number;
  title?: string;
  emptyHint?: string;
  className?: string;
  defaultPreview?: string;
  value?: VideoUploaderValue | null;
  showTemplateButton?: boolean;
  defaultTemplateSubjectType?: string;
  showTiktokButton?: boolean;
  maxClipDuration?: number;
  recommendedMinClipDuration?: number;
  recommendedMaxClipDuration?: number;
  openClipEditorSignal?: number;
  /** Pre-fill TikTok URL and auto-open dialog */
  tiktokInitialUrl?: string;
  onChange?: (value: VideoUploaderValue | null) => void;
}
interface UploadItem extends VideoUploaderValue {
  file?: File;
  uploadKey?: string;
}
const MIN_VIDEO_DURATION = 3; // Minimum video duration in seconds

const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video metadata'));
    };
    video.src = URL.createObjectURL(file);
  });
};
const getVideoDurationFromUrl = (url: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve(video.duration);
    };
    video.onerror = () => {
      reject(new Error('Failed to load video metadata from URL'));
    };
    video.src = url;
  });
};
const formatBytes = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};
const uploadVideoFile = async (file: File) => {
  const md5 = await computeFileMD5(file);
  const res = await fetch('/api/storage/presign', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      md5,
      contentType: file.type
    })
  });
  if (!res.ok) {
    throw new Error(`Presign failed with status ${res.status}`);
  }
  const result = await res.json();
  if (result.code !== 0 || !result.data) {
    throw new Error(result.message || 'Presign failed');
  }
  const {
    data
  } = result;
  if (data.deduplicated) {
    return data.publicUrl as string;
  }
  const putRes = await fetch(data.presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': file.type
    }
  });
  if (!putRes.ok) {
    throw new Error(`Upload to R2 failed with status ${putRes.status}`);
  }
  return data.publicUrl as string;
};
export function VideoUploader(t0) {
  const $ = _c(94);
  const {
    maxSizeMB: t1,
    title,
    emptyHint,
    className,
    defaultPreview,
    value,
    showTemplateButton,
    defaultTemplateSubjectType,
    showTiktokButton,
    maxClipDuration: t2,
    recommendedMinClipDuration,
    recommendedMaxClipDuration,
    openClipEditorSignal,
    tiktokInitialUrl,
    onChange
  } = t0;
  const maxSizeMB = t1 === undefined ? 100 : t1;
  const maxClipDuration = t2 === undefined ? 30 : t2;
  const t = useTranslations("common.uploader");
  const {
    setIsShowSignModal
  } = useAppContext();
  const inputRef = useRef(null);
  const isInitializedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const dragCounterRef = useRef(0);
  const previewVideoRef = useRef(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isTiktokDialogOpen, setIsTiktokDialogOpen] = useState(false);
  const [isClipEditorOpen, setIsClipEditorOpen] = useState(false);
  const lastOpenClipEditorSignalRef = useRef(openClipEditorSignal);
  const tiktokAutoOpenedRef = useRef(false);
  let t3;
  let t4;
  if ($[0] !== tiktokInitialUrl) {
    t3 = () => {
      if (tiktokInitialUrl && !tiktokAutoOpenedRef.current) {
        tiktokAutoOpenedRef.current = true;
        setIsTiktokDialogOpen(true);
      }
    };
    t4 = [tiktokInitialUrl];
    $[0] = tiktokInitialUrl;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  const [isPreviewMuted, setIsPreviewMuted] = useState(true);
  let t5;
  if ($[3] !== defaultPreview) {
    t5 = () => {
      if (defaultPreview) {
        return {
          id: `preset-${defaultPreview}`,
          preview: defaultPreview,
          url: defaultPreview,
          originalUrl: defaultPreview,
          status: "uploaded" as VideoUploadStatus
        };
      }
      return null;
    };
    $[3] = defaultPreview;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const [item, setItem] = useState(t5);
  const maxBytes = maxSizeMB * 1024 * 1024;
  let t6;
  if ($[5] !== item?.status || $[6] !== openClipEditorSignal) {
    t6 = () => {
      if (typeof openClipEditorSignal !== "number" || openClipEditorSignal === lastOpenClipEditorSignalRef.current) {
        return;
      }
      lastOpenClipEditorSignalRef.current = openClipEditorSignal;
      if (item?.status === "uploaded") {
        setIsClipEditorOpen(true);
      }
    };
    $[5] = item?.status;
    $[6] = openClipEditorSignal;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = item?.status;
  let t8;
  if ($[8] !== openClipEditorSignal || $[9] !== t7) {
    t8 = [t7, openClipEditorSignal];
    $[8] = openClipEditorSignal;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  useEffect(t6, t8);
  let t10;
  let t9;
  if ($[11] !== onChange) {
    t9 = () => {
      onChangeRef.current = onChange;
    };
    t10 = [onChange];
    $[11] = onChange;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[14] !== item) {
    t11 = () => () => {
      if (item?.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
    };
    t12 = [item];
    $[14] = item;
    $[15] = t11;
    $[16] = t12;
  } else {
    t11 = $[15];
    t12 = $[16];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[17] !== item) {
    t13 = () => {
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        return;
      }
      if (item) {
        onChangeRef.current?.({
          id: item.id,
          preview: item.preview,
          url: item.url,
          originalUrl: item.originalUrl,
          originalDuration: item.originalDuration,
          status: item.status,
          size: item.size,
          duration: item.duration,
          clipStart: item.clipStart,
          clipEnd: item.clipEnd
        });
      } else {
        onChangeRef.current?.(null);
      }
    };
    t14 = [item];
    $[17] = item;
    $[18] = t13;
    $[19] = t14;
  } else {
    t13 = $[18];
    t14 = $[19];
  }
  useEffect(t13, t14);
  let t15;
  if ($[20] !== item || $[21] !== value) {
    t15 = () => {
      if (value === undefined) {
        return;
      }
      if (value === null) {
        if (item?.preview.startsWith("blob:")) {
          URL.revokeObjectURL(item.preview);
        }
        setItem(null);
        return;
      }
      if (item?.id !== value.id || item?.url !== value.url) {
        if (item?.preview.startsWith("blob:")) {
          URL.revokeObjectURL(item.preview);
        }
        const newItem = {
          id: value.id,
          preview: value.preview,
          url: value.url,
          originalUrl: value.originalUrl,
          originalDuration: value.originalDuration,
          status: value.status,
          size: value.size,
          duration: value.duration,
          clipStart: value.clipStart,
          clipEnd: value.clipEnd
        };
        setItem(newItem);
        if (value.url && (!value.duration || Number.isInteger(value.duration))) {
          getVideoDurationFromUrl(value.url).then(preciseDuration => {
            setItem(prev => prev?.id === newItem.id ? {
              ...prev,
              duration: preciseDuration,
              originalDuration: prev.originalDuration || preciseDuration
            } : prev);
          }).catch(_temp);
        }
      }
    };
    $[20] = item;
    $[21] = value;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== value) {
    t16 = [value];
    $[23] = value;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  useEffect(t15, t16);
  let t17;
  if ($[25] !== item) {
    t17 = async template => {
      if (item?.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
      let duration = template.duration;
      try {
        duration = await getVideoDurationFromUrl(template.videoUrl);
      } catch {}
      setItem({
        id: `template-${template.id}`,
        preview: template.previewUrl || template.videoUrl,
        url: template.videoUrl,
        originalUrl: template.videoUrl,
        originalDuration: duration,
        status: "uploaded",
        duration
      });
    };
    $[25] = item;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  const handleTemplateSelect = t17;
  let t18;
  if ($[27] !== item) {
    t18 = async result => {
      if (item?.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
      let duration_0 = result.duration;
      try {
        duration_0 = await getVideoDurationFromUrl(result.url);
      } catch {}
      setItem({
        id: `tiktok-${Date.now()}`,
        preview: result.url,
        url: result.url,
        originalUrl: result.url,
        originalDuration: duration_0,
        status: "uploaded",
        duration: duration_0,
        size: result.size
      });
    };
    $[27] = item;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  const handleTiktokResolved = t18;
  let t19;
  if ($[29] !== item || $[30] !== maxBytes || $[31] !== maxSizeMB || $[32] !== t) {
    t19 = async file => {
      const validTypes = ["video/mp4", "video/quicktime"];
      if (!validTypes.includes(file.type)) {
        toast.error(t("video_format_unsupported"));
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
      if (file.size > maxBytes) {
        toast.error(t("video_exceeds_size", {
          maxSizeMB
        }));
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
      let duration_1;
      ;
      try {
        duration_1 = await getVideoDuration(file);
        if (duration_1 < MIN_VIDEO_DURATION) {
          toast.error(t("video_too_short", {
            minDuration: MIN_VIDEO_DURATION
          }));
          if (inputRef.current) {
            inputRef.current.value = "";
          }
          return;
        }
      } catch (t20) {
        const error = t20;
        console.error("Failed to get video duration:", error);
        toast.error(t("video_metadata_failed"));
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        return;
      }
      const uploadKey = `${Date.now()}-${Math.random()}`;
      const preview = URL.createObjectURL(file);
      if (item?.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
      const newItem_0 = {
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        preview,
        file,
        size: file.size,
        duration: duration_1,
        status: "uploading",
        uploadKey
      };
      setItem(newItem_0);
      uploadVideoFile(file).then(url => {
        setItem(current => {
          if (!current || current.uploadKey !== uploadKey) {
            return current;
          }
          if (current.preview.startsWith("blob:")) {
            URL.revokeObjectURL(current.preview);
          }
          return {
            ...current,
            preview: url,
            url,
            originalUrl: url,
            originalDuration: current.duration,
            status: "uploaded",
            file: undefined
          };
        });
      }).catch(error_0 => {
        console.error("Video upload failed:", error_0);
        toast.error(error_0?.message ? t("upload_failed_with_reason", {
          reason: error_0.message
        }) : t("upload_failed"));
        setItem(current_0 => {
          if (!current_0 || current_0.uploadKey !== uploadKey) {
            return current_0;
          }
          return {
            ...current_0,
            status: "error"
          };
        });
      }).finally(() => {
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      });
    };
    $[29] = item;
    $[30] = maxBytes;
    $[31] = maxSizeMB;
    $[32] = t;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  const handleFile = t19;
  let t20;
  if ($[34] !== handleFile) {
    t20 = event => {
      const file_0 = event.target.files?.[0];
      if (!file_0) {
        return;
      }
      handleFile(file_0);
    };
    $[34] = handleFile;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const handleSelect = t20;
  let t21;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      dragCounterRef.current = dragCounterRef.current + 1;
      setIsDragActive(true);
    };
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  const handleDragEnter = t21;
  let t22;
  if ($[37] !== isDragActive) {
    t22 = event_1 => {
      event_1.preventDefault();
      event_1.stopPropagation();
      event_1.dataTransfer.dropEffect = "copy";
      if (!isDragActive) {
        setIsDragActive(true);
      }
    };
    $[37] = isDragActive;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  const handleDragOver = t22;
  let t23;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = event_2 => {
      event_2.preventDefault();
      event_2.stopPropagation();
      dragCounterRef.current = dragCounterRef.current - 1;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDragActive(false);
      }
    };
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  const handleDragLeave = t23;
  let t24;
  if ($[40] !== handleFile || $[41] !== t) {
    t24 = event_3 => {
      event_3.preventDefault();
      event_3.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragActive(false);
      const validDropTypes = ["video/mp4", "video/quicktime"];
      const file_1 = Array.from(event_3.dataTransfer?.files || []).find(f => validDropTypes.includes(f.type));
      if (!file_1) {
        toast.error(t("video_format_unsupported"));
        return;
      }
      handleFile(file_1);
    };
    $[40] = handleFile;
    $[41] = t;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  const handleDrop = t24;
  let t25;
  if ($[43] !== item) {
    t25 = () => {
      if (item?.preview.startsWith("blob:")) {
        URL.revokeObjectURL(item.preview);
      }
      setItem(null);
    };
    $[43] = item;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  const handleRemove = t25;
  let t26;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      inputRef.current?.click();
    };
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  const openFilePicker = t26;
  const clipSourceUrl = item?.originalUrl || item?.url || item?.preview || "";
  const t27 = isDragActive && "ring-primary/70 ring-offset-background ring-2 ring-offset-2 ring-inset";
  let t28;
  if ($[46] !== className || $[47] !== t27) {
    t28 = cn("focus-visible:ring-primary/50 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-inset", t27, className);
    $[46] = className;
    $[47] = t27;
    $[48] = t28;
  } else {
    t28 = $[48];
  }
  let t29;
  if ($[49] !== isDragActive || $[50] !== t) {
    t29 = isDragActive && <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-black/10 backdrop-blur-sm"><div className="bg-background/80 text-foreground rounded-full px-4 py-2 text-sm font-medium shadow-sm">{t("drop_to_upload")}</div></div>;
    $[49] = isDragActive;
    $[50] = t;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  let t30;
  if ($[52] !== handleSelect) {
    t30 = <input ref={inputRef} type="file" accept=".mp4,.mov,video/mp4,video/quicktime" onChange={handleSelect} className="hidden" />;
    $[52] = handleSelect;
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] !== title) {
    t31 = title && <div className="text-foreground mb-2 flex items-center justify-between text-sm font-medium"><div className="flex items-center gap-2"><Video className="text-primary h-4 w-4" /><span>{title}</span></div></div>;
    $[54] = title;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  let t32;
  if ($[56] !== clipSourceUrl || $[57] !== emptyHint || $[58] !== handleRemove || $[59] !== isPreviewMuted || $[60] !== item || $[61] !== showTemplateButton || $[62] !== showTiktokButton || $[63] !== t) {
    t32 = item ? <div className="group border-border bg-muted/50 hover:border-border hover:bg-muted relative h-40 overflow-hidden rounded-xl border p-1 shadow-sm transition"><div className="relative h-full overflow-hidden rounded-lg"><video ref={previewVideoRef} src={item.preview} className="h-full w-full rounded-lg object-contain" muted={true} autoPlay={true} loop={true} playsInline={true} /><div className="absolute bottom-2 left-2 flex gap-1">{item.size && <span className="border-border bg-background text-muted-foreground rounded-md border px-2 py-1 text-[10px] font-medium">{formatBytes(item.size)}</span>}{item.duration && <span className="border-border bg-background text-muted-foreground rounded-md border px-2 py-1 text-[10px] font-medium">{item.duration.toFixed(1)} s</span>}</div><VideoZoomButton src={item.preview} className="absolute top-2 left-2 z-20 h-6 w-6" /><button type="button" className="absolute right-2 bottom-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white/90 transition-colors hover:bg-black/80" onClick={e => {
          e.stopPropagation();
          const video = previewVideoRef.current;
          if (!video) {
            return;
          }
          video.muted = !video.muted;
          setIsPreviewMuted(video.muted);
        }} aria-label={isPreviewMuted ? "Unmute" : "Mute"}>{isPreviewMuted ? <VolumeOff className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}</button>{item.status === "uploaded" && clipSourceUrl && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/35 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"><Button type="button" size="icon" variant="secondary" className="bg-background/70 text-foreground hover:bg-background/80 h-10 w-10 rounded-full shadow-sm backdrop-blur focus-visible:ring-2 focus-visible:ring-white/70" onClick={() => {
            setIsClipEditorOpen(true);
          }} aria-label="Edit reference clip"><Scissors className="h-5 w-5" /></Button></div>}{item.status === "uploading" && <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 text-xs font-medium text-white">{t("uploading")}</div>}{item.status === "error" && <div className="absolute inset-0 z-10 flex items-center justify-center bg-red-500/70 text-xs font-medium text-white">{t("failed")}</div>}<Button size="icon" variant="destructive" className="absolute top-2 right-2 z-20 h-7 w-7" onClick={handleRemove} aria-label="Remove video"><IconX className="h-4 w-4" /></Button></div></div> : <div className="group border-border bg-muted/50 hover:border-border hover:bg-muted relative h-40 overflow-hidden rounded-xl border border-dashed p-1 shadow-sm transition"><div className="relative flex h-full flex-col overflow-hidden rounded-lg"><button type="button" className="focus-visible:ring-primary/50 flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-2 rounded-lg px-4 py-3 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset" onClick={openFilePicker}><div className="border-border group-hover:border-primary flex h-10 w-10 items-center justify-center rounded-full border border-dashed transition-colors"><IconUpload className="group-hover:text-primary h-5 w-5 transition-colors" /></div><span className="text-xs font-medium">{t("upload_video")}</span>{emptyHint && <span className="text-muted-foreground line-clamp-2 text-center text-xs">{emptyHint}</span>}</button>{(showTemplateButton || showTiktokButton) && <><div className="flex items-center gap-2 px-4 pb-1"><div className="border-border flex-1 border-t" /><span className="text-muted-foreground text-[10px]">{t("or")}</span><div className="border-border flex-1 border-t" /></div><div className="flex items-center justify-center gap-3 pb-3">{showTemplateButton && <button type="button" className="text-primary hover:text-primary/80 focus-visible:ring-primary/50 rounded-sm text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset" onClick={e_0 => {
              e_0.stopPropagation();
              setIsTemplateDialogOpen(true);
            }}>{t("browse_templates")}</button>}{showTemplateButton && showTiktokButton && <span className="text-muted-foreground text-xs">|</span>}{showTiktokButton && <button type="button" className="text-primary hover:text-primary/80 focus-visible:ring-primary/50 rounded-sm text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset" onClick={e_1 => {
              e_1.stopPropagation();
              setIsTiktokDialogOpen(true);
            }}>{t("paste_tiktok_link")}</button>}</div></>}</div></div>;
    $[56] = clipSourceUrl;
    $[57] = emptyHint;
    $[58] = handleRemove;
    $[59] = isPreviewMuted;
    $[60] = item;
    $[61] = showTemplateButton;
    $[62] = showTiktokButton;
    $[63] = t;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== defaultTemplateSubjectType || $[66] !== handleTemplateSelect || $[67] !== isTemplateDialogOpen || $[68] !== showTemplateButton) {
    t33 = showTemplateButton && <TemplatePickerDialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen} onSelect={handleTemplateSelect} initialSubjectType={defaultTemplateSubjectType} />;
    $[65] = defaultTemplateSubjectType;
    $[66] = handleTemplateSelect;
    $[67] = isTemplateDialogOpen;
    $[68] = showTemplateButton;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== handleTiktokResolved || $[71] !== isTiktokDialogOpen || $[72] !== setIsShowSignModal || $[73] !== showTiktokButton || $[74] !== tiktokInitialUrl) {
    t34 = showTiktokButton && <TikTokLinkDialog open={isTiktokDialogOpen} onOpenChange={setIsTiktokDialogOpen} onResolved={handleTiktokResolved} onAuthRequired={() => setIsShowSignModal(true)} initialUrl={tiktokInitialUrl} />;
    $[70] = handleTiktokResolved;
    $[71] = isTiktokDialogOpen;
    $[72] = setIsShowSignModal;
    $[73] = showTiktokButton;
    $[74] = tiktokInitialUrl;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== clipSourceUrl || $[77] !== isClipEditorOpen || $[78] !== item || $[79] !== maxClipDuration || $[80] !== recommendedMaxClipDuration || $[81] !== recommendedMinClipDuration) {
    t35 = item && <VideoClipEditorDialog open={isClipEditorOpen} onOpenChange={setIsClipEditorOpen} sourceUrl={clipSourceUrl} sourceDuration={item.originalDuration || item.duration} initialStart={item.clipStart} initialEnd={item.clipEnd} maxDuration={maxClipDuration} recommendedMinDuration={recommendedMinClipDuration} recommendedMaxDuration={recommendedMaxClipDuration} onUseClip={clip => {
      setItem(current_1 => {
        if (!current_1) {
          return current_1;
        }
        const originalDuration = current_1.originalDuration || current_1.duration;
        const estimatedSize = current_1.size && originalDuration ? Math.round(current_1.size * clip.duration / originalDuration) : undefined;
        return {
          ...current_1,
          id: `clip-${Date.now()}`,
          preview: clip.preview,
          url: clip.url,
          originalUrl: clip.sourceUrl,
          originalDuration,
          status: "uploaded",
          size: estimatedSize,
          duration: clip.duration,
          clipStart: clip.start,
          clipEnd: clip.end
        };
      });
    }} />;
    $[76] = clipSourceUrl;
    $[77] = isClipEditorOpen;
    $[78] = item;
    $[79] = maxClipDuration;
    $[80] = recommendedMaxClipDuration;
    $[81] = recommendedMinClipDuration;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== handleDragOver || $[84] !== handleDrop || $[85] !== t28 || $[86] !== t29 || $[87] !== t30 || $[88] !== t31 || $[89] !== t32 || $[90] !== t33 || $[91] !== t34 || $[92] !== t35) {
    t36 = <div className={t28} tabIndex={0} onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>{t29}{t30}{t31}{t32}{t33}{t34}{t35}</div>;
    $[83] = handleDragOver;
    $[84] = handleDrop;
    $[85] = t28;
    $[86] = t29;
    $[87] = t30;
    $[88] = t31;
    $[89] = t32;
    $[90] = t33;
    $[91] = t34;
    $[92] = t35;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  return t36;
}
function _temp() {}